import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from '../../../components/form/form.component';
import {
  createExpense,
  updateExpense,
} from '../../../redux/thunks/admin/expenses';
import {
  expenseForm,
  monthlySalaryExpenseForm,
  expenseCategories,
  recurringFrequencies,
} from '../../../utils/form-configs/expenses.form';

import { ReactComponent as LockedIcon } from '../../../assets/icons/forms/locked.svg';
import { ReactComponent as UnlockedIcon } from '../../../assets/icons/forms/unlocked.svg';

const ExpenseModal = ({ show, onClose, type, expense }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [isLocked, setIsLocked] = useState(false);
  const { isCreating, isUpdating } = useSelector(
    (state) => state.entities.admin.expenses
  );

  const isMonthlySalary = type === 'userMonthlySalary';

  useEffect(() => {
    if (expense && isMonthlySalary) {
      setIsLocked(true);
    }
  }, [isMonthlySalary, expense]);

  const getDefaultValues = useCallback(() => {
    if (!expense) {
      return isMonthlySalary
        ? {
            type: 'userMonthlySalary',
            name: 'Monthly Salary',
            category: expenseCategories[1].value,
            recurringFrequency: recurringFrequencies[1].value,
            user: null,
            amount: '',
            startedAt: null,
            finishedAt: null,
          }
        : {
            name: '',
            amount: '',
            category: expenseCategories[0].value,
            recurringFrequency: recurringFrequencies[0].value,
            startedAt: null,
            finishedAt: null,
          };
    }

    const additionalValues = {};

    if (expense.finishedAt) {
      additionalValues.finishedAt = new Date(expense.finishedAt)
        .toISOString()
        .split('T')[0];
    }

    return isMonthlySalary
      ? {
          type: expense.type,
          name: expense.name,
          category: expense.category,
          recurringFrequency: expense.recurringFrequency,
          user: [expense.user],
          amount: expense.amount,
          startedAt: new Date(expense.startedAt).toISOString().split('T')[0],
        }
      : {
          name: expense.name,
          amount: expense.amount,
          category: expense.category,
          recurringFrequency: expense.recurringFrequency,
          startedAt: new Date(expense.startedAt).toISOString().split('T')[0],
          ...additionalValues,
        };
  }, [type, expense]);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    watch(() => clearErrors('invalid'));
  }, [watch, clearErrors]);

  const onSubmit = (formData) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      onClose();
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    if (!formData.finishedAt) {
      delete formData.finishedAt;
    }

    if (isMonthlySalary && formData.user && formData.user.length) {
      if (typeof formData.user[0] === 'string') {
        formData.userId = formData.user[0];
      } else {
        formData.userId = formData.user[0]._id;
      }

      delete formData.user;
    }

    if (!expense) {
      dispatch(
        createExpense({
          formData: {
            ...formData,
          },
          onSuccess,
          onFailure,
        })
      );
    } else {
      dispatch(
        updateExpense({
          formData: {
            _id: expense._id,
            ...formData,
          },
          onSuccess,
          onFailure,
        })
      );
    }
  };

  const form = Object.entries(
    isMonthlySalary ? monthlySalaryExpenseForm : expenseForm
  ).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: {
        ...value,
        readOnly: isLocked,
      },
    };
  }, {});

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <span>
            {expense ? 'Edit' : 'Add'}{' '}
            {isMonthlySalary ? 'Monthly Salary' : ' Expense'}
          </span>
          {isMonthlySalary && (
            <span
              onClick={() => setIsLocked((prev) => !prev)}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isLocked ? (
                <LockedIcon
                  style={{
                    cursor: 'pointer',
                    width: '1.5rem',
                    height: '1.5rem',
                  }}
                />
              ) : (
                <UnlockedIcon
                  style={{
                    cursor: 'pointer',
                    width: '1.5rem',
                    height: '1.5rem',
                  }}
                />
              )}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          form={form}
          useForm={{
            register,
            errors,
            setValue,
            getValues,
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit(onSubmit)}
          disabled={
            isLocked || (!expense ? isCreating : isUpdating[expense._id])
          }
        >
          {expense ? 'Edit' : 'Add'}{' '}
          {isMonthlySalary ? 'Monthly Salary' : ' Expense'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExpenseModal;
